import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Drivers } from '@ionic/storage';
import { Storage } from '@ionic/storage-angular';
import * as moment from 'moment';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

interface CachedData {
  created: number;
  exp: number;
  status: number;
  data: string;
}

@Injectable({
  providedIn: 'root',
})
export class DataCacheStorageService {
  private _cacheDb: Storage;

  constructor(private httpClient: HttpClient) {
    this.initStorage();
  }

  private initStorage() {
    this._cacheDb = new Storage({
      name: '__cache_db',
      storeName: '_cache',
      driverOrder: [Drivers.SecureStorage, Drivers.IndexedDB],
    });
    this._cacheDb.create();
  }

  private decodeData(data: string, encoded = true): Record<string, any> {
    if (!data) return null;

    try {
      if (encoded === false) {
        return JSON.parse(data);
      }
      const decodedStr = atob(data);
      return JSON.parse(decodedStr);
    } catch (err) {
      return null;
    }
  }

  private encodeData(data: Record<string, any>, encode = true) {
    try {
      const objString = JSON.stringify(data);
      if (encode === true) {
        return btoa(objString);
      }
      return objString;
    } catch (err) {
      return null;
    }
  }

  /**
   * Request GET to the url, with caching response data in storage.
   * It will fetch from storage if the data in storage is available and not yet expired.
   * @param url - target API URL to be fetched.
   * @param queries - URL queries
   * @param bypassCache - Bypass caching by requesting GET data without caching to the API. Defaults to false.
   * @returns - Fetched data from the API/Cache.
   */
  public getData(
    url: string,
    queries?: Record<string, string>,
    bypassCache = false,
    options?: { expireInMinutes?: number; encodeBase64?: boolean }
  ): Observable<Record<string, any>> {
    const queryParams = new URLSearchParams(queries).toString();
    const targetURL = url + `${queryParams ? '?' + queryParams : ''}`;

    return from(this._cacheDb.get(targetURL) as Promise<CachedData>).pipe(
      take(1),
      switchMap((storageData: CachedData) => {
        if (storageData && moment(storageData.exp).isBefore(moment())) {
          // Cache is expired
          return from(this._cacheDb.remove(targetURL)).pipe(map(() => null));
        }
        return of(storageData || null);
      }),
      switchMap((storageData: CachedData) => {
        if (storageData?.data) {
          const decodedData = this.decodeData(storageData.data, options?.encodeBase64 !== false);
          return of(decodedData);
        } else {
          return of(null);
        }
      }),
      switchMap((decodedData: Record<string, any>) => {
        if (!decodedData || bypassCache === true) {
          return this.httpClient.get(targetURL, { observe: 'response' }).pipe(
            tap(({ body, status }) => {
              const encodedData = this.encodeData(body, options?.encodeBase64 !== false);

              if (encodedData === null) return this._cacheDb.remove(targetURL);
              this._cacheDb.set(targetURL, {
                created: moment().valueOf(),
                exp: moment()
                  .add(options?.expireInMinutes || 1440, 'minutes')
                  .valueOf(),
                data: encodedData,
                status,
              } as CachedData);
            }),
            map(({ body }) => body)
          );
        } else {
          return of(decodedData);
        }
      }),
      catchError((err) => throwError(err))
    );
  }

  /**
   * Clear the storage with targetURL key, use when something error after fetching the data.
   * @param targetURL - target url.
   */
  public resetStorage(targetURL: string) {
    return this._cacheDb.remove(targetURL);
  }

  /**
   * Clear all keys on the storage.
   * Careful to use the method, as it will clear all data on the storage.
   */
  public clear(): Promise<void> {
    return this._cacheDb.clear();
  }
}
